// @intent: Pixi-backed implementation of the renderer-neutral PlayableAPI facade
// @flow: runtime creates adapter with project/render model -> generated setup(api) code can load assets, lookup slots, emit events
// @stale: update when PlayableAPI adds methods or runtime starts executing agent-authored code

import {
  getDefaultPlayableCapabilityManifest,
  layoutHelpers,
  type JsonObject,
  type JsonValue,
  type PlayableAPI,
  type PlayableLayerRef,
  type PlayableLayerSetProps,
  type PlayablePlaybackHandle,
  type PlayablePointerEventType,
  type PlayablePointerHandler,
  type PlayableSlotRef,
  type PlayableTweenHandle,
  type PlayableTweenOptions,
  type PlayableViewport,
  type Project,
  type RenderNode,
  type RenderModel,
  type PlayableTweenProps,
} from '@mintyads/playable-api';
import type { RuntimeInputPayload } from '@mintyads/shared-types';
import { getPixiTexture, isLogicalPixiAssetFileUrl, preloadPixiAssets, resolvePixiAssetUrl } from './pixiAssets';
import { createRuntimeInputHelpers } from './runtimeInput';
import { createRuntimeLoop } from './runtimeLoop';
import { createNumericTween, createRejectedTweenHandle } from './pixiTween';
import type { RuntimeCleanupScope } from './runtimeCleanup';
import { createTweenConflictRegistry, type TweenConflictRegistry } from './tweenConflictRegistry';
import type { RuntimeProbeTweenRegistry } from '../probe/runtimeProbeTweens';

// @intent: Animatable Pixi props a tween can drive AND a layer.set can overwrite. A direct set of any of these,
//   or a newer tween touching them, must cancel an in-flight tween on the same prop+target (the match-3 holes bug).
// @stale Keep in lockstep with createTweenTargetAdapter / applyPixiLayerSetProps + PlayableTweenProperty.
const ANIMATABLE_LAYER_PROPS = ['x', 'y', 'alpha', 'width', 'height', 'rotation', 'scale'] as const;

export interface PixiPlayableApiOptions {
  readonly project: Project;
  readonly renderModel: RenderModel;
  readonly viewport?: PlayableViewport | undefined;
  readonly mode: 'edit' | 'preview';
  readonly emit: (name: string, data?: JsonObject) => void;
  readonly targetRegistry: ReadonlyMap<string, Record<string, unknown>>;
  readonly pendingLayerProps?: Map<string, PlayableLayerSetProps> | undefined;
  readonly assetBaseUrl?: string | undefined;
  readonly onInput?: ((type: PlayablePointerEventType, handler: PlayablePointerHandler) => () => void) | undefined;
  readonly onInputEvidence?: ((evidence: RuntimeInputPayload) => void) | undefined;
  readonly cleanupScope?: RuntimeCleanupScope | undefined;
  readonly probeTweenRegistry?: RuntimeProbeTweenRegistry | undefined;
  readonly probeStateProviders?: Map<string, () => unknown> | undefined;
}

interface LayerCoordinateSpace {
  readonly parentWorldX: number;
  readonly parentWorldY: number;
}

interface PixiUniformScaleTarget {
  x?: number;
  y?: number;
  set(scale: number): void;
}

function createTimestampId(prefix: string): string {
  return `${prefix}-${Date.now().toString()}`;
}

function isPixiUniformScaleTarget(value: unknown): value is PixiUniformScaleTarget {
  return value !== null
    && typeof value === 'object'
    && 'set' in value
    && typeof value.set === 'function';
}

/**
 * @intent Per-scale-object capture of the MOUNT display scale so `layer.set({ scale })` is RELATIVE to the node's
 *          rendered size, not the raw texture size.
 * @flow A sprite is sized to its node width/height at mount; Pixi expresses that as a fractional scale (e.g. a
 *        606px texture in a 74px box => scale ~0.12). The first scale op captures that base; later ops apply
 *        scale*base. Keyed on the scale ObservablePoint (one per sprite) so it is per-layer and GC-friendly.
 * @stale If a layer's width/height is re-set after a scale op, the base becomes stale (rare); recapture on demand.
 */
const layerBaseScales = new WeakMap<object, { x: number; y: number }>();

function applyPixiUniformScale(target: Record<string, unknown>, scale: number): void {
  const scaleTarget = target['scale'];
  if (isPixiUniformScaleTarget(scaleTarget)) {
    // `scale` is RELATIVE to the mount display size. A naive absolute scaleTarget.set(1) would blow a
    // texture-sized sprite up to its full natural resolution (the "ассеты огромные" giant-sprite bug,
    // 2026-05-29): the node was rendered at e.g. 74px via a ~0.12 mount scale, and scale:1 discarded it.
    const sx = typeof scaleTarget.x === 'number' ? scaleTarget.x : 1;
    const sy = typeof scaleTarget.y === 'number' ? scaleTarget.y : sx;
    let base = layerBaseScales.get(scaleTarget);
    if (base === undefined) {
      base = { x: sx, y: sy };
      layerBaseScales.set(scaleTarget, base);
    }
    scaleTarget.x = scale * base.x;
    scaleTarget.y = scale * base.y;
    return;
  }
  target['scale'] = scale;
}

function resetLayerBaseScale(target: Record<string, unknown>): void {
  const scaleTarget = target['scale'];
  if (scaleTarget && typeof scaleTarget === 'object') {
    layerBaseScales.delete(scaleTarget);
  }
}

function pixiUniformScaleValue(target: Record<string, unknown>): number {
  const scaleTarget = target['scale'];
  if (typeof scaleTarget === 'number') return scaleTarget;
  if (scaleTarget && typeof scaleTarget === 'object' && 'x' in scaleTarget && typeof scaleTarget.x === 'number') {
    return scaleTarget.x;
  }
  return 1;
}

function createTweenTargetAdapter(target: Record<string, unknown>, props: PlayableTweenProps): Record<string, number> {
  const adapted: Record<string, number> = {};
  for (const key of Object.keys(props)) {
    if (key === 'scale') {
      adapted.scale = pixiUniformScaleValue(target);
      continue;
    }
    const value = target[key];
    adapted[key] = typeof value === 'number' ? value : 0;
  }
  return adapted;
}

function flushTweenAdapter(target: Record<string, unknown>, adapted: Record<string, number>): void {
  for (const [key, value] of Object.entries(adapted)) {
    if (key === 'scale') {
      applyPixiUniformScale(target, value);
      continue;
    }
    target[key] = value;
  }
}

function createDeferredTweenHandle(input: {
  readonly id: string;
  readonly requestFrame: (callback: FrameRequestCallback) => number;
  readonly cancelFrame: (handle: number) => void;
  readonly createMountedTween: () => PlayableTweenHandle | null;
}): PlayableTweenHandle {
  let frameId: number | null = null;
  let innerHandle: PlayableTweenHandle | null = null;
  let stopped = false;
  let settled = false;
  let resolveFinished!: () => void;
  let rejectFinished!: (reason: unknown) => void;

  const finished = new Promise<void>((resolve, reject) => {
    resolveFinished = resolve;
    rejectFinished = reject;
  });

  const resolveOnce = () => {
    if (settled) return;
    settled = true;
    resolveFinished();
  };
  const rejectOnce = (reason: unknown) => {
    if (settled) return;
    settled = true;
    rejectFinished(reason);
  };

  const tick = () => {
    if (stopped || settled) return;
    const mountedTween = input.createMountedTween();
    if (mountedTween) {
      innerHandle = mountedTween;
      void mountedTween.finished.then(resolveOnce, rejectOnce);
      return;
    }
    frameId = input.requestFrame(tick);
  };

  frameId = input.requestFrame(tick);

  return {
    id: input.id,
    finished,
    stop: () => {
      if (stopped || settled) return;
      stopped = true;
      if (frameId !== null) input.cancelFrame(frameId);
      innerHandle?.stop();
      resolveOnce();
    },
  };
}

export function applyPixiLayerSetProps(target: Record<string, unknown>, props: PlayableLayerSetProps): boolean {
  let textureApplied = true;
  if (props.assetUrl !== undefined || props.width !== undefined || props.height !== undefined) {
    resetLayerBaseScale(target);
  }

  // Apply the texture FIRST. In Pixi v8 assigning sprite.texture RESETS width/height to the texture's natural
  // size (and `sprite.width = w` computes scale relative to the CURRENT texture). If width/height were set before
  // the texture, the node's box (e.g. 74x74) is overwritten by the generated image's natural size (e.g. 1024px) —
  // the "ассеты огромные" giant-sprite bug (2026-05-29). Applying texture first lets the dimensions below win.
  if (props.assetUrl !== undefined) {
    target['assetUrl'] = props.assetUrl;
    const texture = getPixiTexture(props.assetUrl);
    if (texture !== null) {
      target['texture'] = texture;
    } else {
      textureApplied = false;
    }
  }

  for (const key of ['x', 'y', 'alpha', 'width', 'height', 'rotation'] as const) {
    const value = props[key];
    if (typeof value === 'number') target[key] = value;
  }

  if (typeof props.text === 'string') target['text'] = props.text;

  if (typeof props.visible === 'boolean') target['visible'] = props.visible;

  if (typeof props.scale === 'number') applyPixiUniformScale(target, props.scale);

  if (props.tint !== undefined) target['tint'] = props.tint;

  return textureApplied;
}

function collectKnownLayerIds(node: RenderNode, ids = new Set<string>()): Set<string> {
  ids.add(node.id);
  if (node.type === 'container') {
    for (const child of node.children) collectKnownLayerIds(child, ids);
  }
  return ids;
}

function collectLayerCoordinateSpaces(
  node: RenderNode,
  spaces = new Map<string, LayerCoordinateSpace>(),
): Map<string, LayerCoordinateSpace> {
  spaces.set(node.id, {
    parentWorldX: node.worldX - node.x,
    parentWorldY: node.worldY - node.y,
  });
  if (node.type === 'container') {
    for (const child of node.children) collectLayerCoordinateSpaces(child, spaces);
  }
  return spaces;
}

function rememberPendingLayerProps(
  pendingLayerProps: Map<string, PlayableLayerSetProps> | undefined,
  id: string,
  props: PlayableLayerSetProps,
): void {
  if (!pendingLayerProps) return;
  pendingLayerProps.set(id, { ...pendingLayerProps.get(id), ...props });
}

function resolveLayerSetProps(props: PlayableLayerSetProps, assetBaseUrl: string | undefined): PlayableLayerSetProps {
  if (props.assetUrl === undefined) return props;
  const assetUrl = resolvePixiAssetUrl(props.assetUrl, assetBaseUrl);
  return assetUrl === props.assetUrl ? props : { ...props, assetUrl };
}

function resolveLayerPositionToPixiLocal(
  props: PlayableLayerSetProps,
  space: LayerCoordinateSpace | undefined,
): PlayableLayerSetProps {
  if (!space) return props;
  const next = { ...props };
  if (typeof props.x === 'number') next.x = props.x - space.parentWorldX;
  if (typeof props.y === 'number') next.y = props.y - space.parentWorldY;
  return next;
}

function resolveTweenPositionToPixiLocal(
  props: PlayableTweenProps,
  space: LayerCoordinateSpace | undefined,
): PlayableTweenProps {
  if (!space) return props;
  const next = { ...props };
  if (typeof props.x === 'number') next.x = props.x - space.parentWorldX;
  if (typeof props.y === 'number') next.y = props.y - space.parentWorldY;
  return next;
}

function createMountedTweenHandle(input: {
  readonly id: string;
  readonly mutableTarget: Record<string, unknown>;
  readonly props: PlayableTweenProps;
  readonly options: PlayableTweenOptions;
  readonly space: LayerCoordinateSpace | undefined;
  readonly cleanupScope: RuntimeCleanupScope | undefined;
  readonly conflictRegistry: TweenConflictRegistry | undefined;
  readonly probeTweenRegistry: RuntimeProbeTweenRegistry | undefined;
  readonly targetLayerId: string;
}): PlayableTweenHandle {
  const resolvedProps = resolveTweenPositionToPixiLocal(input.props, input.space);
  const adaptedTarget = createTweenTargetAdapter(input.mutableTarget, resolvedProps);
  const handle = createNumericTween({
    id: input.id,
    target: adaptedTarget,
    props: resolvedProps,
    options: input.options,
    requestFrame: window.requestAnimationFrame.bind(window),
    cancelFrame: window.cancelAnimationFrame.bind(window),
    now: () => performance.now(),
    onUpdate: () => { flushTweenAdapter(input.mutableTarget, adaptedTarget); },
    probeTargetLayerId: input.targetLayerId,
    onProbeUpdate: (snapshot) => { input.probeTweenRegistry?.upsert(snapshot); },
  });
  const tracked = input.cleanupScope?.trackTween(handle) ?? handle;
  // Register this tween's props on the REAL target so a later tween OR a direct layer.set on the same prop cancels
  // it (prevents overlapping never-stopped tweens from overriding an authoritative set — the match-3 holes bug).
  if (input.conflictRegistry) {
    const writtenProps = new Set(Object.keys(resolvedProps));
    input.conflictRegistry.registerTween(input.mutableTarget, writtenProps, tracked);
    void tracked.finished
      .finally(() => { input.conflictRegistry?.releaseTween(input.mutableTarget, tracked); })
      .catch(() => undefined);
  }
  return tracked;
}

export function createPixiPlayableApi(options: PixiPlayableApiOptions): PlayableAPI {
  const knownLayerIds = collectKnownLayerIds(options.renderModel.root);
  const layerCoordinateSpaces = collectLayerCoordinateSpaces(options.renderModel.root);
  const designViewport = options.renderModel.viewport;
  const renderedViewport = options.viewport ?? designViewport;
  const layerTextureLoads = new Map<string, Promise<void>>();
  const ensureLayerTextureLoaded = (assetUrl: string): Promise<void> => {
    const current = layerTextureLoads.get(assetUrl);
    if (current) return current;
    const next = preloadPixiAssets([assetUrl])
      .finally(() => { layerTextureLoads.delete(assetUrl); });
    layerTextureLoads.set(assetUrl, next);
    return next;
  };
  const resolveAssetUrl = (url: string): string => resolvePixiAssetUrl(url, options.assetBaseUrl);
  const input = createRuntimeInputHelpers({
    onPointer: (type, handler) => options.onInput?.(type, handler) ?? (() => {}),
    onInputEvidence: options.onInputEvidence,
    cleanupScope: options.cleanupScope,
    now: () => Date.now(),
  });
  const loop = createRuntimeLoop({ cleanupScope: options.cleanupScope });
  const probe = {
    registerStateProvider: (name: string, provider: () => JsonValue): (() => void) => {
      const key = name.trim();
      if (!key) throw new Error('probe state provider name is required');
      options.probeStateProviders?.set(key, provider);
      const cleanup = () => {
        if (options.probeStateProviders?.get(key) === provider) {
          options.probeStateProviders.delete(key);
        }
      };
      return options.cleanupScope?.trackCleanup(cleanup) ?? cleanup;
    },
    mark: (label: string, data?: JsonObject): void => {
      options.emit('probe_mark', {
        label,
        ...(data === undefined ? {} : { data }),
      });
    },
  };
  // One per setup() run. Lets a later write (a newer tween OR a direct layer.set) on a prop cancel the in-flight
  // tween still driving that prop on the same target, so the most recent intent wins (match-3 holes bug fix).
  const conflictRegistry = createTweenConflictRegistry();

  return {
    app: {
      projectId: options.project.id,
      viewport: designViewport,
      designViewport,
      renderedViewport,
      mode: options.mode,
      now: () => Date.now(),
    },
    capabilities: getDefaultPlayableCapabilityManifest(),
    layout: layoutHelpers,
    input,
    loop,
    probe,
    loadTexture: async (url) => {
      const resolvedUrl = resolveAssetUrl(url);
      await preloadPixiAssets([resolvedUrl]);
      return { type: 'texture', id: resolvedUrl, url: resolvedUrl };
    },
    loadSpritesheet: async (url) => {
      const resolvedUrl = resolveAssetUrl(url);
      if (isLogicalPixiAssetFileUrl(resolvedUrl)) {
        throw new Error('logical app asset files are texture images; spritesheets require extension-based JSON asset URLs');
      }
      await preloadPixiAssets([resolvedUrl]);
      return { type: 'spritesheet', id: resolvedUrl, url: resolvedUrl };
    },
    play: (): PlayablePlaybackHandle => ({
      id: createTimestampId('play'),
      stop: () => {},
      finished: Promise.resolve(),
    }),
    tween: (target, props, tweenOptions) => {
      const mutableTarget = options.targetRegistry.get(target.id);
      if (!mutableTarget) {
        if (!knownLayerIds.has(target.id)) {
          return createRejectedTweenHandle(`tween-missing-${target.id}`, `unknown tween target: ${target.id}`);
        }

        const deferredHandle = createDeferredTweenHandle({
          id: `tween-pending-${target.id}-${Date.now().toString()}`,
          requestFrame: window.requestAnimationFrame.bind(window),
          cancelFrame: window.cancelAnimationFrame.bind(window),
          createMountedTween: () => {
            const mountedTarget = options.targetRegistry.get(target.id);
            if (!mountedTarget) return null;
            return createMountedTweenHandle({
              id: `tween-${target.id}-${Date.now().toString()}`,
              mutableTarget: mountedTarget,
              props,
              options: tweenOptions,
              space: layerCoordinateSpaces.get(target.id),
              cleanupScope: undefined,
              conflictRegistry,
              probeTweenRegistry: options.probeTweenRegistry,
              targetLayerId: target.id,
            });
          },
        });
        return options.cleanupScope?.trackTween(deferredHandle) ?? deferredHandle;
      }

      return createMountedTweenHandle({
        id: `tween-${target.id}-${Date.now().toString()}`,
        mutableTarget,
        props,
        options: tweenOptions,
        space: layerCoordinateSpaces.get(target.id),
        cleanupScope: options.cleanupScope,
        conflictRegistry,
        probeTweenRegistry: options.probeTweenRegistry,
        targetLayerId: target.id,
      });
    },
    layer: (id): PlayableLayerRef | null => {
      if (!knownLayerIds.has(id) && !options.targetRegistry.has(id)) return null;
      return {
        type: 'layer',
        id,
        set: (props) => {
          const resolvedProps = resolveLayerSetProps(props, options.assetBaseUrl);
          const localProps = resolveLayerPositionToPixiLocal(resolvedProps, layerCoordinateSpaces.get(id));
          const target = options.targetRegistry.get(id);
          if (!target) {
            rememberPendingLayerProps(options.pendingLayerProps, id, localProps);
            if (localProps.assetUrl !== undefined) {
              void ensureLayerTextureLoaded(localProps.assetUrl)
                .then(() => {
                  const mountedTarget = options.targetRegistry.get(id);
                  if (mountedTarget) applyPixiLayerSetProps(mountedTarget, localProps);
                })
                .catch(() => undefined);
            }
            return;
          }
          // A direct set of an animatable prop is authoritative: cancel any in-flight tween still driving that prop
          // on this target so the set value isn't immediately overwritten by the next tween tick. This is what makes
          // refreshBoardLayers' alpha:1 / x / y reset win over a still-running clear-fade or swap-slide tween
          // (the match-3 holes bug: refilled tiles were being driven back to alpha:0 and stayed invisible).
          const setAnimatableProps = ANIMATABLE_LAYER_PROPS.filter((key) => localProps[key] !== undefined);
          if (setAnimatableProps.length > 0) {
            conflictRegistry.cancelTweensForProps(target, setAnimatableProps);
          }
          const textureApplied = applyPixiLayerSetProps(target, localProps);
          if (!textureApplied && localProps.assetUrl !== undefined) {
            void ensureLayerTextureLoaded(localProps.assetUrl)
              .then(() => {
                const currentTarget = options.targetRegistry.get(id);
                if (currentTarget) applyPixiLayerSetProps(currentTarget, localProps);
              })
              .catch(() => undefined);
          }
        },
      };
    },
    slot: (id): PlayableSlotRef | null => {
      const slot = options.project.slot_map.slots[id];
      if (!slot) return null;
      return {
        type: 'slot',
        id: slot.layerId,
        slotId: id,
        label: slot.label,
        layerId: slot.layerId,
        slotKind: slot.kind,
      };
    },
    emit: options.emit,
  };
}
